import { Grid, useTheme } from "@mui/material";
import React from "react";
import {
  Bar,
  BarChart as ReBarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  evaluationTypes,
  perspective_colors,
  perspectives,
} from "../../lib/diagramTypes";
import { perspectiveLabels } from "../../lib/diagramTypes";
import FullScreenLoader from "../FullScreenLoader";

export default function BarChart({
  survey_data,
  evaluationType,
  questions,
  subdimension,
  all_perspectives,
  displayedPerspectives,
  displayQuestionDetails,
  displaySubdimensionDetails,
}) {
  const theme = useTheme();

  if (survey_data)
    return (
      <Grid container direction="column" spacing={2} sx={{ margin: 1 }}>
        <Grid item>
          <Grid container>
            <Grid
              item
              xs={
                (displayedPerspectives.includes(perspectives.teacher) ||
                  displayedPerspectives.includes(perspectives.expert)) &&
                !displayedPerspectives.includes(perspectives.student)
                  ? 12
                  : (displayedPerspectives.includes(perspectives.teacher) ||
                      displayedPerspectives.includes(perspectives.expert)) &&
                    displayedPerspectives.includes(perspectives.student)
                  ? 6
                  : 0
              }
              sx={{
                transition: theme.transitions.create([
                  "flex-basis",
                  "max-width",
                ]),
                ...(!displayedPerspectives.includes(perspectives.teacher) &&
                  !displayedPerspectives.includes(perspectives.expert) && {
                    display: "none",
                  }),
              }}
            >
              <h2 className="mb-2 mt-6 pl-4 text-lg font-semibold">
                Einschätzung des Angebots
              </h2>
            </Grid>
            <Grid
              item
              xs={
                (displayedPerspectives.includes(perspectives.teacher) ||
                  displayedPerspectives.includes(perspectives.expert)) &&
                !displayedPerspectives.includes(perspectives.student)
                  ? 0
                  : (displayedPerspectives.includes(perspectives.teacher) ||
                      displayedPerspectives.includes(perspectives.expert)) &&
                    displayedPerspectives.includes(perspectives.student)
                  ? 6
                  : 12
              }
              sx={{
                transition: theme.transitions.create([
                  "flex-basis",
                  "max-width",
                ]),
                ...(!displayedPerspectives.includes(perspectives.student) && {
                  display: "none",
                }),
              }}
            >
              <h2 className="mb-2 mt-6 pl-4 text-lg font-semibold">
                Einschätzung der Nutzung
              </h2>
            </Grid>
          </Grid>
        </Grid>
        {survey_data.evaluation[evaluationType].map((evaluation) => (
          <Grid item key={evaluation.questionId}>
            <Grid container direction="row" alignItems="center" spacing={2}>
              <Grid item xs={2}>
                {evaluationType === evaluationTypes.question ? (
                  <span
                    onClick={() =>
                      displayQuestionDetails(
                        questions.results.find(
                          (q) => q.id === evaluation.questionId
                        )["name"]
                      )
                    }
                  >
                    {
                      questions.results.find(
                        (q) => q.id === evaluation.questionId
                      )["title"]
                    }
                  </span>
                ) : (
                  <span
                    onClick={() =>
                      displaySubdimensionDetails(evaluation.subdmension_name)
                    }
                  >
                    {
                      subdimension.results.find(
                        (s) => evaluation.subdmension_name === s.name
                      )["title"]
                    }
                  </span>
                )}
              </Grid>
              <Grid item xs={11}>
                <Grid container>
                  <Grid
                    item
                    xs={
                      (displayedPerspectives.includes(perspectives.teacher) ||
                        displayedPerspectives.includes(perspectives.expert)) &&
                      !displayedPerspectives.includes(perspectives.student)
                        ? 12
                        : (displayedPerspectives.includes(
                            perspectives.teacher
                          ) ||
                            displayedPerspectives.includes(
                              perspectives.expert
                            )) &&
                          displayedPerspectives.includes(perspectives.student)
                        ? 6
                        : 0
                    }
                    sx={{
                      transition: theme.transitions.create([
                        "flex-basis",
                        "max-width",
                      ]),
                      ...(!displayedPerspectives.includes(
                        perspectives.teacher
                      ) &&
                        !displayedPerspectives.includes(
                          perspectives.expert
                        ) && {
                          display: "none",
                        }),
                    }}
                  >
                    <ResponsiveContainer width="100%" height={200}>
                      <ReBarChart
                        width={400}
                        height={300}
                        margin={{
                          top: 20,
                          right: 20,
                          left: 20,
                          bottom: 20,
                        }}
                        data={[1, 2, 3, 4].map((possibleValue) => ({
                          name: possibleValue,
                          [perspectives.teacher]:
                            evaluation["L"][`score__${possibleValue}__count`] /
                            evaluation["L"][`score__total__count`],
                          [perspectives.expert]:
                            evaluation["E"][`score__${possibleValue}__count`] /
                            evaluation["E"][`score__total__count`],
                        }))}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis
                          domain={[0, 1]}
                          tickFormatter={(tick) => {
                            return `${tick * 100}%`;
                          }}
                        />
                        <Tooltip
                          formatter={(value, name, props) => [
                            `${parseFloat((value * 100).toPrecision(3))}%`,
                            name,
                          ]}
                        />
                        <Legend
                          payload={[
                            {
                              dataKey: perspectives.teacher,
                              type: "rect",
                              color: perspective_colors[perspectives.teacher],
                              value: perspectiveLabels.get(
                                perspectives.teacher
                              ),
                            },
                            {
                              dataKey: perspectives.expert,
                              type: "rect",
                              color: perspective_colors[perspectives.expert],
                              value: perspectiveLabels.get(perspectives.expert),
                            },
                          ]}
                        />
                        <Bar
                          dataKey={perspectives.teacher}
                          fill={perspective_colors[perspectives.teacher]}
                          maxBarSize={
                            displayedPerspectives.includes(perspectives.teacher)
                              ? ""
                              : 0
                          }
                        />
                        <Bar
                          dataKey={perspectives.expert}
                          fill={perspective_colors[perspectives.expert]}
                          maxBarSize={
                            displayedPerspectives.includes(perspectives.expert)
                              ? ""
                              : 0
                          }
                        />
                      </ReBarChart>
                    </ResponsiveContainer>
                  </Grid>
                  <Grid
                    item
                    xs={
                      (displayedPerspectives.includes(perspectives.teacher) ||
                        displayedPerspectives.includes(perspectives.expert)) &&
                      !displayedPerspectives.includes(perspectives.student)
                        ? 0
                        : (displayedPerspectives.includes(
                            perspectives.teacher
                          ) ||
                            displayedPerspectives.includes(
                              perspectives.expert
                            )) &&
                          displayedPerspectives.includes(perspectives.student)
                        ? 6
                        : 12
                    }
                    sx={{
                      transition: theme.transitions.create([
                        "flex-basis",
                        "max-width",
                      ]),
                      ...(!displayedPerspectives.includes(
                        perspectives.student
                      ) && {
                        display: "none",
                      }),
                    }}
                  >
                    <ResponsiveContainer width="100%" height={200}>
                      <ReBarChart
                        width={730}
                        height={250}
                        margin={{
                          top: 20,
                          right: 20,
                          left: 20,
                          bottom: 20,
                        }}
                        data={[1, 2, 3, 4].map((possibleValue) => ({
                          name: possibleValue,
                          [perspectives.student]:
                            evaluation["S"][`score__${possibleValue}__count`] /
                            evaluation["S"][`score__total__count`],
                        }))}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis
                          domain={[0, 1]}
                          tickFormatter={(tick) => {
                            return `${tick * 100}%`;
                          }}
                        />
                        <Tooltip
                          formatter={(value, name, props) => [
                            `${parseFloat((value * 100).toPrecision(3))}%`,
                            name,
                          ]}
                        />
                        <Legend
                          payload={[
                            {
                              dataKey: perspectives.student,
                              type: "rect",
                              color: perspective_colors[perspectives.student],
                              value: perspectiveLabels.get(
                                perspectives.student
                              ),
                            },
                          ]}
                        />
                        <Bar
                          dataKey={perspectives.student}
                          fill={perspective_colors[perspectives.student]}
                          maxBarSize={
                            displayedPerspectives.includes(perspectives.student)
                              ? ""
                              : 0
                          }
                        />
                      </ReBarChart>
                    </ResponsiveContainer>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Grid>
    );
  else return <FullScreenLoader />;
}
